// src/components/layout/Sidebar.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Shield,
  FolderTree,
  Package,
  UtensilsCrossed,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Ticket,
  HelpingHand,
  ChartCandlestick,
  NotebookPen,
  Logs,
  PersonStandingIcon,
  PercentSquareIcon,
  User,
  Settings,
  FileMinus,
  UserRound,
  FileText,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import Logo from 'assets/logo.png';
import { ProtectedSidebarItem } from '../common/ProtectedSidebarItem';
import { useHasPermission } from '../../hooks/useHasPermission';

const AnimatedIcon = ({ icon: Icon, className = "" }) => {
  const hoverAnimations = {
    Settings: "hover:animate-spin",
    LayoutDashboard: "hover:animate-bounce",
    Ticket: "hover:animate-pulse",
    Logs: "hover:animate-wobble",
    UtensilsCrossed: "hover:animate-ping",
    HelpingHand: "hover:animate-wave",
    NotebookPen: "hover:animate-write",
    default: "hover:scale-125 hover:rotate-12"
  };

  const animationClass =
    hoverAnimations[Icon.displayName || Icon.name] ||
    hoverAnimations.default;

  return (
    <Icon className={cn("w-5 h-5 transition-all duration-300", animationClass, className)} />
  );
};

const Sidebar = ({
  isCollapsed,
  setIsCollapsed,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null); // Manages which top-level dropdown is open
  const [openReportsSubmenu, setOpenReportsSubmenu] = useState(null); // Manages which Reports sub-dropdown is open
  const hasPermission = useHasPermission();

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', tooltip: 'Dashboard Overview', resource: 'dashboard' },
    {
      name: 'Manage Employee',
      icon: Users,
      isDropdown: true,
      tooltip: 'Employee Management',
      children: [
        { name: 'Employee', path: '/employee', tooltip: 'Manage Employees', resource: 'employee' },
        { name: 'Role', path: '/role', tooltip: 'Manage Roles', resource: 'role' },
        { name: 'Department', path: '/department', tooltip: 'Manage Departments', resource: 'department' },
        { name: 'Company', path: '/company', tooltip: 'Manage Companies', resource: 'company' },
      ],
    },
    { name: 'My Teams', icon: UserRound, path: '/my-teams', tooltip: 'Teams Overview' },
     { name: 'My Attendance', icon: UserRound, path: '/my-attendance', tooltip: 'Attendance Overview' },
    {
      name: 'Manage Category',
      icon: FolderTree,
      isDropdown: true,
      tooltip: 'Category Management',
      children: [
        { name: 'Category', path: '/category', tooltip: 'Manage Categories', resource: 'category' },
        { name: 'Subcategory', path: '/subcategory', tooltip: 'Manage Subcategories', resource: 'sub_category' },
      ],
    },
    {
      name: 'Manage Inventory',
      icon: Package,
      isDropdown: true,
      tooltip: 'Inventory Management',
      children: [
        { name: 'Vendor', path: '/vendor', tooltip: 'Manage Vendor', resource: 'vendor' },
        { name: 'Inventory', path: '/inventory', tooltip: 'Manage Inventory', resource: 'inventory' },
        { name: 'Stock', path: '/stock', tooltip: 'Manage Stock', resource: 'stock' },
      ],
    },
    {
      name: 'Manage Canteen',
      icon: UtensilsCrossed,
      isDropdown: true,
      tooltip: 'Canteen Management',
      children: [
        { name: 'Menu (Place Order)', path: '/canteen/menu', tooltip: 'Manage Menu', resource: 'menu' },
        { name: 'Wallet', path: '/update-wallet', tooltip: 'Wallet', resource: 'employee_wallet_history' },
        { name: 'Order List', path: '/order-list', tooltip: 'Order List', resource: 'canteen_order_log' },
        { name: 'Break Time', path: '/break-time-log', tooltip: 'Break Time', resource: 'break_time_log' },
        { name: 'Feedback', path: '/feedback', tooltip: 'Feedback', resource: 'feedback' },
      ],
    },
    { name: 'Request Issuance', icon: HelpingHand, path: '/request-issuance', tooltip: 'Request Issuance', resource: 'request' },
    { name: 'Raise Ticket', icon: Ticket, path: '/raise-ticket', tooltip: 'Raise Ticket', resource: 'ticket' },
    { name: 'IT Asset Management', icon: ChartCandlestick, path: '/asset-assign', tooltip: 'Asset Assign', resource: 'asset_assign' },
    { name: 'Suggestion', icon: NotebookPen, path: '/suggestion', tooltip: 'Suggestion', resource: 'suggestions' },
    {
      name: 'System Logs',
      icon: Logs,
      isDropdown: true,
      tooltip: 'Logs Management',
      children: [
        { name: 'General Log', path: '/general-log', tooltip: 'General Log', resource: 'activity_logs' },
        { name: 'Employee Log', path: '/employee-log', tooltip: 'Employee Log', resource: 'employee_log' },
        { name: 'Permission Log', path: '/permissions-log', tooltip: 'Permission Log', resource: 'permission_log' },
        { name: 'Break Time Log', path: '/break-log', tooltip: 'Break Time Log', resource: 'break_log' },
        { name: 'Escalation Log', path: '/escalation-history-log', tooltip: 'Escalation Log', resource: 'escalation_history' },
        { name: 'Inventory Log', path: '/inventory-log', tooltip: 'Inventory Log', resource: 'inventory_log' },
        { name: 'Request Log', path: '/request-logs', tooltip: 'Request Log', resource: 'request_log' },
        { name: 'Asset Assign Log', path: '/asset-assign-logs', tooltip: 'Asset Assign Log', resource: 'asset_assign_log' },
        { name: 'Ticket Log', path: '/ticket-logs', tooltip: 'Ticket Log', resource: 'ticket_log' },
        { name: 'Canteen Log', path: '/canteen-logs', tooltip: 'Canteen Log', resource: 'canteen_log' },
      ],
    },
    {
      name: 'Reports',
      icon: FileText,
      isDropdown: true,
      tooltip: 'Reports',
      resource: 'reports',
      children: [
        {
          name: 'Inventory Reports',
          parentName: 'Reports', // Added for exclusive logic
          isDropdown: true,
          tooltip: 'Inventory Management Reports',
          resource: 'reports',
          children: [
            { name: 'Current Inventory Summary', path: '/reports/inventory-summary', tooltip: 'Current Inventory Summary Report', resource: 'reports' },
            { name: 'Stock Movement', path: '/reports/stock-movement', tooltip: 'Daily/Weekly/Monthly Stock Movement Report', resource: 'reports' },
            { name: 'Low Stock Alert', path: '/reports/low-stock', tooltip: 'Low Stock Alert Report', resource: 'reports' },
            { name: 'Expired Items', path: '/reports/expired-items', tooltip: 'Expired / Near Expiry Items Report', resource: 'reports' },
            { name: 'Consumption Trend', path: '/reports/consumption-trend', tooltip: 'Item Consumption Trend Report', resource: 'reports' },
            { name: 'Department Consumption', path: '/reports/department-consumption', tooltip: 'Department-wise Consumption Report', resource: 'reports' },
            { name: 'Purchase History', path: '/reports/purchase-history', tooltip: 'Purchase History Report', resource: 'reports' },
            { name: 'Price Variation', path: '/reports/price-variation', tooltip: 'Price Variation Report', resource: 'reports' },
          ],
        },
        {
          name: 'Asset Reports',
          parentName: 'Reports', // Added for exclusive logic
          isDropdown: true,
          tooltip: 'Asset Management Reports',
          resource: 'reports',
          children: [
            { name: 'Asset Master', path: '/reports/asset-master', tooltip: 'Asset Master Report', resource: 'reports' },
            // { name: 'Warranty Expiry', path: '/reports/warranty-expiry', tooltip: 'Warranty & AMC Expiry Report', resource: 'reports' },
            // { name: 'Asset Allocation', path: '/reports/asset-allocation', tooltip: 'Asset Allocation Report', resource: 'reports' },
            // { name: 'Idle Assets', path: '/reports/idle-assets', tooltip: 'Idle / Unused Assets Report', resource: 'reports' },
            // { name: 'Asset Lifecycle Cost', path: '/reports/asset-lifecycle-cost', tooltip: 'Asset Lifecycle Cost Report', resource: 'reports' },
          ],
        },
        {
          name: 'Break Reports',
          parentName: 'Reports', // Added for exclusive logic
          isDropdown: true,
          tooltip: 'Employee Break Management Reports',
          resource: 'reports',
          children: [
            { name: 'Break Summary', path: '/reports/break-summary', tooltip: 'Break Punch In / Punch Out Summary Report', resource: 'reports' },
            { name: 'Break Duration', path: '/reports/break-duration', tooltip: 'Total Daily/Monthly Break Duration Report', resource: 'reports' },
            { name: 'Irregular Breaks', path: '/reports/irregular-breaks', tooltip: 'Irregular Punch Activity Report', resource: 'reports' },
            { name: 'Time Deviation', path: '/reports/time-deviation', tooltip: 'Time Deviation Report', resource: 'reports' },
          ],
        },
        {
          name: 'Ticketing Reports',
          parentName: 'Reports', // Added for exclusive logic
          isDropdown: true,
          tooltip: 'Ticketing / Internal Issue Management Reports',
          resource: 'reports',
          children: [
            { name: 'Tickets Overview', path: '/reports/tickets-overview', tooltip: 'Open / Closed Tickets Report', resource: 'reports' },
            { name: 'TAT Compliance', path: '/reports/tat-compliance', tooltip: 'TAT Compliance Report', resource: 'reports' },
            { name: 'Escalations', path: '/reports/escalations', tooltip: 'Escalation Report', resource: 'reports' },
            { name: 'Department Tickets', path: '/reports/department-tickets', tooltip: 'Department-wise Ticket Volume Report', resource: 'reports' },
            { name: 'Recurring Issues', path: '/reports/recurring-issues', tooltip: 'Recurring Issue Report', resource: 'reports' },
            { name: 'Handler Productivity', path: '/reports/handler-productivity', tooltip: 'Ticket Handler Productivity Report', resource: 'reports' },
          ],
        },
        {
          name: 'Canteen Reports',
          parentName: 'Reports', // Added for exclusive logic
          isDropdown: true,
          tooltip: 'Canteen Management Reports',
          resource: 'reports',
          children: [
            { name: 'Canteen order summary', path: '/reports/canteen-orders', tooltip: 'Canteen Order Summary Report', resource: 'reports' },
            { name: 'Popular Items', path: '/reports/popular-items', tooltip: 'Popular & Least Popular Items Report', resource: 'reports' },
            { name: 'Canteen Finance', path: '/reports/canteen-finance', tooltip: 'Canteen Expense vs. Collection Report', resource: 'reports' },
            { name: 'Ingredient Stock', path: '/reports/ingredient-stock', tooltip: 'Ingredient Stock Report', resource: 'reports' },
          ],
        },
      ],
    },
    { name: 'Settings', icon: Settings, path: '/settings', tooltip: 'Settings' },
    { name: 'Customer Profile', icon: User, path: 'https://clients.myvaluetrips.co.in', tooltip: 'Customer Profile', resource: 'customer_profile', isExternal: true },
  ];

  const shouldShowDropdown = (item) => {
    if (!item.children) return false;
    const checkChildrenPermission = (children) => {
      if (!children) return false;
      return children.some(child => {
        if (child.resource && hasPermission(child.resource)) {
          return true;
        }
        if (child.children) {
          return checkChildrenPermission(child.children);
        }
        return false;
      });
    };
    return checkChildrenPermission(item.children);
  };

  const shouldShowItem = (item) => {
    if (!item.resource) return true;
    return hasPermission(item.resource);
  };

  const closeMobile = () => setIsMobileOpen(false);

  return (
    <>
      {/* Mobile Drawer */}
      <div className={cn(
        'fixed inset-0 z-50 md:hidden flex flex-col',
        isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        'transition-transform duration-300 ease-in-out'
      )}>
        <div className="absolute inset-0 bg-black/50" onClick={closeMobile} />
        <div className="relative flex flex-col w-64 max-w-full h-full bg-gradient-to-b from-slate-900 to-slate-950 shadow-2xl">
          <div className="flex items-center justify-between px-5 py-5 border-b border-white/10 flex-shrink-0">
            <Link to="/dashboard" className="bg-white rounded-sm flex items-center gap-3" onClick={closeMobile}>
              <img src={Logo} alt="Shree Sati 360" className="p-2 h-12 w-auto" />
            </Link>
            <button onClick={closeMobile} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          <nav className="sidebar-scroll flex-1 overflow-y-auto px-4 py-4 space-y-1 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {menuItems.map((item) => {
              if (item.isDropdown && !shouldShowDropdown(item)) return null;
              if (!item.isDropdown && !shouldShowItem(item)) return null;
              return (
                <ProtectedSidebarItem
                  key={item.name}
                  item={item}
                  isCollapsed={false}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  onItemClick={closeMobile}
                  iconComponent={AnimatedIcon}
                  openReportsSubmenu={openReportsSubmenu}
                  setOpenReportsSubmenu={setOpenReportsSubmenu}
                />
              );
            })}
          </nav>
          <div className="p-5 border-t border-white/10 flex-shrink-0">
            <div className="text-xs text-white/60">
              <p className="font-medium text-white">Shree Sati 360</p>
              <p>© {new Date().getFullYear()} All rights reserved</p>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className={cn(
        'fixed left-0 top-0 z-40 h-screen transition-all duration-300 hidden md:flex flex-col bg-gradient-to-b from-slate-900 to-slate-950',
        isCollapsed ? 'w-17' : 'w-64'
      )}>
        <div className="flex items-center justify-between px-4 py-5 h-16 border-b border-white/10">
          {!isCollapsed && (
            // className=' mx-auto mt-[-2px]'
            <Link to="/dashboard" className='bg-white rounded-sm'>
              <img src={Logo} alt="Shree Sati 360" className="p-2 h-12 w-auto" />
            </Link>
          )}
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-2 rounded-lg hover:bg-white/10 transition-colors">
            {isCollapsed ? <Menu className="w-5 h-5 text-white" /> : <X className="w-5 h-5 text-white" />}
          </button>
        </div>
        <nav className="sidebar-scroll flex-1 overflow-y-auto p-3 space-y-1">
          <TooltipProvider delayDuration={0}>
            {menuItems.map((item) => {
              if (item.isDropdown && !shouldShowDropdown(item)) return null;
              if (!item.isDropdown && !shouldShowItem(item)) return null;
              return (
                <ProtectedSidebarItem
                  key={item.name}
                  item={item}
                  isCollapsed={isCollapsed}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  iconComponent={AnimatedIcon}
                  openReportsSubmenu={openReportsSubmenu}
                  setOpenReportsSubmenu={setOpenReportsSubmenu}
                />
              );
            })}
          </TooltipProvider>
        </nav>
        {!isCollapsed && (
          <div className="p-4 border-t border-white/10">
            <div className="text-xs text-white/60">
              <p className="font-medium">Shree Sati 360</p>
              <p>© {new Date().getFullYear()} All rights reserved</p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;